import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Divider,
  IconButton,
  InputAdornment,
  Snackbar,
  Alert,
  CircularProgress,
  LinearProgress,
  Chip,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  ArrowBack,
  Save,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { updatePassword } from "../services/passwordService.tsx";

const ChangePassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const existingPassword = location.state?.currentPassword || "";

  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "", // Always empty initially
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleTogglePassword = (field: keyof typeof showPassword) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const validatePassword = (password: string) => {
    const requirements = [
      { regex: /.{8,}/, message: "Minimum 8 characters" },
      { regex: /[A-Z]/, message: "At least one uppercase letter" },
      { regex: /[a-z]/, message: "At least one lowercase letter" },
      { regex: /[0-9]/, message: "At least one number" },
      { regex: /[^A-Za-z0-9]/, message: "At least one special character" },
    ];

    const errors = requirements
      .filter((req) => !req.regex.test(password))
      .map((req) => req.message);

    return {
      isValid: errors.length === 0,
      messages: errors,
    };
  };

  // Update the validateForm function
  const validateForm = () => {
    let valid = true;
    const newErrors = {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    };

    // Current password validation
    if (!passwordData.currentPassword) {
      newErrors.currentPassword = "Current password is required";
      valid = false;
    }

    // New password validation
    const passwordValidation = validatePassword(passwordData.newPassword);
    if (!passwordValidation.isValid) {
      newErrors.newPassword = passwordValidation.messages.join(", ");
      valid = false;
    }

    // Confirm password validation
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      await updatePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      // Show success and clear form
      setNotification({
        open: true,
        message: "Password updated successfully!",
        severity: "success",
      });

      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error: any) {
      setNotification({
        open: true,
        message: error.message,
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const PasswordStrengthMeter = ({ password }: { password: string }) => {
    const requirements = [
      { regex: /.{8,}/, label: "8+ characters" },
      { regex: /[A-Z]/, label: "Uppercase letter" },
      { regex: /[a-z]/, label: "Lowercase letter" },
      { regex: /[0-9]/, label: "Number" },
      { regex: /[^A-Za-z0-9]/, label: "Special character" },
    ];

    const passedRequirements = requirements.filter((r) =>
      r.regex.test(password)
    );
    const strength = passedRequirements.length;

    return (
      <Box sx={{ mt: 1, mb: 2 }}>
        <LinearProgress
          variant="determinate"
          value={(strength / requirements.length) * 100}
          color={
            strength <= 2 ? "error" : strength <= 3 ? "warning" : "success"
          }
          sx={{ height: 6, mb: 1 }}
        />
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {requirements.map((req, i) => (
            <Chip
              key={i}
              label={req.label}
              size="small"
              color={req.regex.test(password) ? "success" : "default"}
              variant="outlined"
            />
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ padding: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }} disabled={isSubmitting}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" fontWeight="bold">
          Change Password
        </Typography>
      </Box>

      {/* Password Form */}
      <Paper sx={{ p: 3, maxWidth: 600, mx: "auto" }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Current Password Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Current Password"
                name="currentPassword"
                type={showPassword.current ? "text" : "password"}
                value={passwordData.currentPassword}
                onChange={handleInputChange}
                error={!!errors.currentPassword}
                helperText={errors.currentPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleTogglePassword("current")}
                        edge="end"
                      >
                        {showPassword.current ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* New Password Field with Strength Meter */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="New Password"
                name="newPassword"
                type={showPassword.new ? "text" : "password"}
                value={passwordData.newPassword}
                onChange={(e) => {
                  handleInputChange(e);
                  // Real-time validation
                  const validation = validatePassword(e.target.value);
                  setErrors((prev) => ({
                    ...prev,
                    newPassword: validation.messages.join(", "),
                  }));
                }}
                error={!!errors.newPassword}
                helperText={errors.newPassword || " "}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleTogglePassword("new")}
                        edge="end"
                      >
                        {showPassword.new ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {/* Password Strength Meter - Only shows when typing */}
              {passwordData.newPassword && (
                <PasswordStrengthMeter password={passwordData.newPassword} />
              )}
            </Grid>

            {/* Confirm New Password Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Confirm New Password"
                name="confirmPassword"
                type={showPassword.confirm ? "text" : "password"}
                value={passwordData.confirmPassword}
                onChange={(e) => {
                  handleInputChange(e);
                  // Real-time confirmation check
                  if (
                    e.target.value &&
                    e.target.value !== passwordData.newPassword
                  ) {
                    setErrors((prev) => ({
                      ...prev,
                      confirmPassword: "Passwords do not match",
                    }));
                  } else {
                    setErrors((prev) => ({ ...prev, confirmPassword: "" }));
                  }
                }}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleTogglePassword("confirm")}
                        edge="end"
                      >
                        {showPassword.confirm ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Save/Cancel Buttons */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 3,
                }}
              >
                {/* <Button
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button> */}
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={
                    isSubmitting ||
                    !passwordData.currentPassword ||
                    !passwordData.newPassword ||
                    !passwordData.confirmPassword ||
                    !!errors.newPassword ||
                    !!errors.confirmPassword
                  }
                  startIcon={
                    isSubmitting ? <CircularProgress size={20} /> : <Save />
                  }
                >
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Password Requirements */}
      <Paper sx={{ p: 3, mt: 3, maxWidth: 600, mx: "auto" }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Password Requirements:
        </Typography>
        <ul style={{ paddingLeft: 20, margin: 0 }}>
          {[
            "Minimum 8 characters",
            "At least one uppercase letter (A-Z)",
            "At least one lowercase letter (a-z)",
            "At least one number (0-9)",
            "At least one special character (!@#$%^&*)",
          ].map((req, i) => (
            <li key={i}>
              <Typography
                variant="body2"
                color={
                  passwordData.newPassword &&
                  validatePassword(passwordData.newPassword).messages.includes(
                    req.split(" (")[0]
                  )
                    ? "error.main"
                    : "text.primary"
                }
              >
                {req}
              </Typography>
            </li>
          ))}
        </ul>
      </Paper>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChangePassword;
